import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppFacadeService } from '@smarsh-cyber/app-state';
import {
  ACCESS_DENIED,
  HEADER,
  isIgnoreHeaders
} from '@smarsh-cyber/shared/constants';
import { Guid } from 'guid-typescript';
import { Observable, catchError, throwError } from 'rxjs';
import { BFF_BASE_URL } from '../../../app.constants';
import {
  GET_TENANT_ID,
  GET_USER_DETAILS_API_PATH
} from '../../../+app_state/utils/app-state.constants';

@Injectable()
export class BFFHttpInterceptor implements HttpInterceptor {
  constructor(
    private appFacade: AppFacadeService,
    private router: Router
  ) {}

  /**
   * 401 / 403
   * logout
   */

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!req.url.includes(BFF_BASE_URL) || isIgnoreHeaders(req.url)) {
      return this.handleRequestWithErrorHandling(req, next);
    }

    const headers = this.getHeaders(req);

    let clonedRequest = req.clone({ setHeaders: headers });

    // Enable credentials for specific routes
    if (
      ['/user', '/device', '/remediation', '/exceptions'].some((path) =>
        req.url.includes(path)
      )
    ) {
      clonedRequest = clonedRequest.clone({ withCredentials: true });
    }

    return this.handleRequestWithErrorHandling(clonedRequest, next);
  }

  private getHeaders(req: HttpRequest<unknown>): Record<string, string> {
    const url = req.url;
    const userToken = this.appFacade?.$userToken();
    const userDetails = this.appFacade?.$userDetails();

    // APIs where we DO NOT want to add tenant/user headers
    const excludedApis: string[] = [GET_TENANT_ID];

    // If URL matches any excluded API, just return correlation header (and existing)
    const isExcluded = excludedApis.some((pattern) => url.includes(pattern));

    // Extract existing headers
    const existingHeaders: Record<string, string> = {};
    req.headers.keys().forEach((key) => {
      existingHeaders[key] = req.headers.get(key)!;
    });

    // Always include correlation ID
    const headers: Record<string, string> = {
      ...existingHeaders,
      [HEADER.CORRELATION_ID]:
        existingHeaders[HEADER.CORRELATION_ID] ?? Guid.create().toString()
    };

    // Skip adding user/tenant headers for excluded APIs
    if (isExcluded) return headers;

    // Add tenant/user headers only if missing
    if (userToken?.tenantId) {
      if (!headers[HEADER.TENANT_ID])
        headers[HEADER.TENANT_ID] = userToken.tenantId;

      if (!headers[HEADER.USER_ID])
        headers[HEADER.USER_ID] = userDetails?.userId ?? '';

      if (userDetails?.businessUnitId && !headers[HEADER.BUID_ID])
        headers[HEADER.BUID_ID] = userDetails.businessUnitId;
    }

    return headers;
  }

  private handleRequestWithErrorHandling(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        //temp handling 403 on loggedin_user user call - need this to handle navigating to logout on ngp
        //remove this on priority once fixed
        if (
          error.status === HttpStatusCode.Forbidden &&
          error.url.includes(GET_USER_DETAILS_API_PATH)
        ) {
          this.appFacade.logout();
          return throwError(() => error);
        }
        //end of temp solution
        return this.handleError(error.status, req.url, error);
      })
    );
  }

  handleError(
    code: number,
    url: string,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (url.includes('/users/login')) return throwError(() => error);

    // Status 0 indicates network error, CORS issue, or request cancellation
    // Do NOT logout on status 0 as it's often a transient network issue
    // Only logout on explicit 401 Unauthorized from the server
    if (code === HttpStatusCode.Unauthorized) {
      console.warn('401 Unauthorized - logging out', { url, code });
      this.appFacade.logout();
      return throwError(() => error);
    } else if (code === HttpStatusCode.Forbidden) {
      console.warn('403 Forbidden - navigating to access denied', {
        url,
        code
      });
      this.router.navigate([`/${ACCESS_DENIED}`], {
        queryParamsHandling: 'merge'
      });
      return throwError(() => error);
    } else if (!code || code === 0) {
      // Network error - throw proper error for translators to handle
      console.error(
        'Network error - request may have been cancelled or network unavailable',
        { url }
      );
      const networkError = new HttpErrorResponse({
        error: { message: 'Network error' },
        status: 0,
        statusText: 'Unknown Error',
        url: url
      });
      return throwError(() => networkError);
    } else {
      // Unexpected error - throw proper error for translators to handle
      console.error('An unexpected error occurred - please try again', {
        url,
        code
      });
      return throwError(() => error);
    }
  }
}
